/** Atalhos usados em quase todas as telas. */
import { useMemo } from 'react';
import { todayIso } from '@dotapps/core';
import { useAuth } from '../app/auth';
import { useClients, useCompanies, useSettings, useStages, useSuppliers, useUsers } from './queries';

/** Usuário logado (garantido dentro da área logada). */
export function useSession() {
  const { user, isAdmin, isSeller, canSeeCost, role } = useAuth();
  return { me: user!, isAdmin, isSeller, canSeeCost, role: role! };
}

/** "Hoje" no fuso do escritório (vem da API; cai para o do navegador enquanto carrega). */
export function useToday(): string {
  return useSettings().data?.today ?? todayIso();
}

/** Nomes e cadastros por id, para montar as telas sem repetir buscas. */
export function useLookups() {
  const users = useUsers().data ?? [];
  const clients = useClients().data ?? [];
  const suppliers = useSuppliers().data ?? [];
  const stages = useStages().data ?? [];
  const companies = useCompanies().data ?? [];
  return useMemo(() => {
    const userById = new Map(users.map(u => [u.id, u]));
    const clientById = new Map(clients.map(c => [c.id, c]));
    const supplierById = new Map(suppliers.map(s => [s.id, s]));
    const companyById = new Map(companies.map(c => [c.id, c]));
    return {
      users,
      clients,
      suppliers,
      stages,
      companies,
      userName: (id: string | null | undefined) => (id && userById.get(id)?.name) || '—',
      userIni: (id: string | null | undefined) => (id && userById.get(id)?.ini) || '?',
      client: (id: string | null | undefined) => (id ? clientById.get(id) : undefined),
      clientName: (id: string | null | undefined) => (id && clientById.get(id)?.nome) || '—',
      supplierName: (id: string | null | undefined) => (id && supplierById.get(id)?.nome) || '—',
      stageName: (key: string) => stages.find(s => s.key === key)?.nome ?? key,
      hasStage: (key: string) => stages.some(s => s.key === key),
      company: (id: string | null | undefined) => (id ? companyById.get(id) : undefined) ?? companies[0],
    };
  }, [users, clients, suppliers, stages, companies]);
}
